import React from 'react';
import Popup from 'reactjs-popup';
import { Worker, Viewer, ProgressBar, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { pdfjs } from 'react-pdf';
import { useTranslate } from '../TranslationText';

export default function PdfViewerPopup({ open, onClose, title, attachmentUrl, canDownloadContent = false }) {
    const { translate } = useTranslate();
    
    const renderToolbar = (Toolbar) => (
        <Toolbar>
            {(slots) => {
                const {
                    CurrentPageInput,
                    Download,
                    EnterFullScreen,
                    GoToNextPage,
                    GoToPreviousPage,
                    NumberOfPages,
                    Print,
                    ShowSearchPopover,
                    Zoom,
                    ZoomIn,
                    ZoomOut,
                } = slots;
                return (
                    <div
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            width: '100%',
                        }}
                    >
                        <div style={{ padding: '0px 2px' }}>
                            <ShowSearchPopover />
                        </div>
                        <div style={{ padding: '0px 2px' }}>
                            <ZoomOut />
                        </div>
                        <div style={{ padding: '0px 2px' }}>
                            <Zoom />
                        </div>
                        <div style={{ padding: '0px 2px' }}>
                            <ZoomIn />
                        </div>
                        <div style={{ padding: '0px 2px', marginLeft: 'auto' }}>
                            <GoToPreviousPage />
                        </div>
                        <div style={{ padding: '0px 2px', display: 'flex', alignItems: 'center' }}>
                            <CurrentPageInput /> / <NumberOfPages />
                        </div>
                        <div style={{ padding: '0px 2px' }}>
                            <GoToNextPage />
                        </div>
                        <div style={{ padding: '0px 2px', marginLeft: 'auto' }}>
                            <EnterFullScreen />
                        </div>
                        {canDownloadContent && (
                            <div style={{ padding: '0px 2px' }}>
                                <Download />
                            </div>
                        )}
                        <div style={{ padding: '0px 2px' }}>
                            <Print />
                        </div>
                    </div>
                );
            }}
        </Toolbar>
    );

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar,
        toolbarPlugin: {
            fullScreenPlugin: {
                getFullScreenTarget: (pagesContainer) => pagesContainer.closest('[data-testid="default-layout__body"]'),
                renderExitFullScreenButton: (props) => <></>,
            },
        },
    });

    const isImage = !!attachmentUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(attachmentUrl);

    const [isDownloading, setIsDownloading] = React.useState(false);

    const handleDirectDownload = async (e) => {
        e.preventDefault();
        if (!attachmentUrl || isDownloading) return;

        try {
            setIsDownloading(true);
            const response = await fetch(attachmentUrl);
            if (!response.ok) throw new Error('Fetch failed');
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = blobUrl;

            const urlParts = attachmentUrl.split('/');
            const originalName = urlParts[urlParts.length - 1].split('?')[0];
            const extension = originalName.includes('.') ? originalName.split('.').pop() : 'pdf';
            const cleanTitle = (title || 'document').replace(/[/\\?%*:|"<>]/g, '').trim();
            a.download = cleanTitle ? `${cleanTitle}.${extension}` : originalName || `document.${extension}`;

            document.body.appendChild(a);
            a.click();

            setTimeout(() => {
                window.URL.revokeObjectURL(blobUrl);
                document.body.removeChild(a);
            }, 200);
        } catch (err) {
            console.error('Direct download error:', err);
            window.open(attachmentUrl, '_blank');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Popup 
            open={open}
            onClose={onClose}
            modal
            closeOnEscape={true}
            closeOnDocumentClick={true}
            lockScroll={true}
            contentStyle={{ width: '98%', height: '90vh', maxWidth: '1600px', display: 'flex', flexDirection: 'column', padding: '0', backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden' }}
            overlayStyle={{ zIndex: 5000, backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
            {(close) => (
                <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '15px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 className="m-0 fw-bold">{title}</h4>
                        <div className="d-flex align-items-center gap-2">
                            {canDownloadContent && !!attachmentUrl && (
                                <button
                                    type="button"
                                    onClick={handleDirectDownload}
                                    className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        minWidth: '32px',
                                        minHeight: '32px',
                                        padding: 0,
                                        margin: 0,
                                        borderRadius: '6px',
                                        lineHeight: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    title="Download"
                                >
                                    {isDownloading ? (
                                        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '13px', lineHeight: 1 }}></i>
                                    ) : (
                                        <svg
                                            width="15"
                                            height="15"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            style={{ display: 'block' }}
                                        >
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7 10 12 15 17 10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                    )}
                                </button>
                            )}
                            <button type="button" className="btn-close" aria-label="Close" onClick={close}></button>
                        </div>
                    </div>
                    
                    <div style={{ flex: 1, position: 'relative', overflow: 'hidden', backgroundColor: '#f0f0f0' }}>
                        {isImage ? (
                            <img src={attachmentUrl} alt={title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                            <Worker workerUrl={`//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`}>
                                <Viewer
                                    renderLoader={(percentages) => (
                                        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                            <div className="spinner-border text-danger" role="status" style={{ width: '3rem', height: '3rem' }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <div className="mt-3 fw-bold text-muted">
                                                {translate('web.loading', 'Loading...')} {Math.round(percentages)}%
                                            </div>
                                        </div>
                                    )}
                                    fileUrl={attachmentUrl}
                                    plugins={[defaultLayoutPluginInstance]}
                                    defaultScale={SpecialZoomLevel.PageFit}
                                />
                            </Worker>
                        )}
                    </div>
                </div>
            )}
        </Popup>
    );
}
